/**
 * First-leak-report onboarding nudges — the encouragement cascade that
 * pushes a new user to their "AHA moment": the first leak report at 25
 * skill_signals.
 *
 * Pure logic + a localStorage high-water mark (per-device, like
 * `DailyChallengeCelebration`'s `pt:daily-celebrated`). No backend: the
 * progress (`signalCount` / 25) already rides on the `["billing-status"]`
 * query, refreshed after every drill.
 *
 * The 25/100% payoff is the existing `FirstLeakReportModal` — these are the
 * intermediate milestones leading up to it, so `pendingMilestone` stops
 * returning anything at 25.
 */

export const FIRST_REPORT_SIGNALS = 25;

export interface NudgeMilestone {
  /** Fires once `signalCount` reaches this. */
  atSignals: number;
  title: string;
  body: string;
}

/** Thresholds ≈ first drill, 25%, 50%, 75% of the 25-signal gate. Copy is
 *  framed as "keep drilling" (signals are the gate, drilling is the verb). */
export const NUDGE_MILESTONES: NudgeMilestone[] = [
  {
    atSignals: 1,
    title: "First read on the board",
    body: "The coach just clocked its first read on you. 24 more and your leak report drops — the patterns quietly bleeding your EV.",
  },
  {
    atSignals: 6,
    title: "A quarter of the way",
    body: "Enough reps for the coach to start seeing the shape of your game. Keep stacking them.",
  },
  {
    atSignals: 13,
    title: "Past halfway",
    body: "Your patterns are taking form. The coach is closing in on your biggest leak — don't cool off now.",
  },
  {
    atSignals: 19,
    title: "Almost there",
    body: "Six more reads and your first leak report drops. You're at the good part.",
  },
];

/**
 * The milestone to celebrate now, or null. Returns the HIGHEST milestone the
 * user has reached that's past `lastCelebrated` (so a drill crossing several
 * at once shows only the top one). Stops at the 25-signal gate — that's the
 * `FirstLeakReportModal`'s payoff, not a nudge.
 *
 * Pure — unit-tested in `firstReportNudge.test.ts`.
 */
export function pendingMilestone(
  signalCount: number,
  lastCelebrated: number,
): NudgeMilestone | null {
  if (signalCount >= FIRST_REPORT_SIGNALS) return null;
  let best: NudgeMilestone | null = null;
  for (const m of NUDGE_MILESTONES) {
    if (m.atSignals <= signalCount && m.atSignals > lastCelebrated) {
      best = m; // milestones are ascending, so the last match is the highest
    }
  }
  return best;
}

const STORAGE_KEY = "pt:first-report-milestone";

/** Highest milestone threshold already celebrated on this device (0 if none
 *  / storage unavailable). */
export function lastCelebratedMilestone(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const n = raw == null ? 0 : parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

/** Record a milestone threshold as celebrated (monotonic high-water mark). */
export function markMilestoneCelebrated(atSignals: number): void {
  try {
    if (atSignals > lastCelebratedMilestone()) {
      localStorage.setItem(STORAGE_KEY, String(atSignals));
    }
  } catch {
    // Storage unavailable — at worst a nudge re-fires; minor.
  }
}
